/**
 * Profit Replay / What-If Simulator Page.
 *
 * Three simulation modes:
 * 1. Single Sale — model selling one asset at a hypothetical price
 * 2. Bulk Liquidation — model selling all inventory at X% markup
 * 3. Replay Sale — replay a completed sale with adjusted pricing
 */

import { useState } from "react";
import { Calculator, Package, RefreshCw } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";

type SimMode = "single" | "bulk" | "replay";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function WhatIfPage() {
  const [mode, setMode] = useState<SimMode>("single");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          What-If Simulator
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Model alternative pricing scenarios without affecting real data.
        </p>
      </div>

      <div className="border-b border-[var(--color-border)]">
        <nav className="flex gap-6">
          {[
            {
              key: "single" as SimMode,
              label: "Single Asset",
              icon: Calculator,
            },
            {
              key: "bulk" as SimMode,
              label: "Bulk Liquidation",
              icon: Package,
            },
            { key: "replay" as SimMode, label: "Replay Sale", icon: RefreshCw },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              className={`flex items-center gap-2 border-b-2 pb-3 text-sm font-medium transition-colors ${mode === tab.key ? "border-brand-600 text-brand-600" : "border-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"}`}
            >
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {mode === "single" && <SingleSaleSimulator />}
      {mode === "bulk" && <BulkLiquidationSimulator />}
      {mode === "replay" && <ReplaySaleSimulator />}
    </div>
  );
}

function SingleSaleSimulator() {
  const [assetId, setAssetId] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [commRate, setCommRate] = useState("0");
  const [taxRate, setTaxRate] = useState("0");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);

  const simulate = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/whatif/single-sale?asset_id=${assetId}&sale_price=${salePrice}&commission_rate=${parseFloat(commRate) / 100}&tax_rate=${parseFloat(taxRate) / 100}`,
        )
      ).data,
    onSuccess: (data) => setResult(data),
  });

  const r = result as Record<string, unknown> | null;

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
          Single Asset Simulation
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 items-end">
          <Input
            label="Asset ID"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            placeholder="UUID"
          />
          <Input
            label="Sale Price ($)"
            type="number"
            step="0.01"
            value={salePrice}
            onChange={(e) => setSalePrice(e.target.value)}
            placeholder="75000"
          />
          <Input
            label="Commission (%)"
            type="number"
            step="0.1"
            value={commRate}
            onChange={(e) => setCommRate(e.target.value)}
          />
          <Input
            label="Tax Rate (%)"
            type="number"
            step="0.1"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            size="sm"
            onClick={() => simulate.mutate()}
            isLoading={simulate.isPending}
            disabled={!assetId || !salePrice}
          >
            <Calculator className="h-4 w-4" /> Simulate
          </Button>
        </div>
      </Card>

      {r && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Hypothetical COGS
            </p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {fmt(r.hypothetical_cogs as number)}
            </p>
            {(r.excluded_costs as number) > 0 && (
              <p className="text-xs text-[var(--text-warning)]">
                −{fmt(r.excluded_costs as number)} excluded
              </p>
            )}
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Gross Profit
            </p>
            <p
              className={`text-2xl font-bold ${(r.gross_profit as number) >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
            >
              {fmt(r.gross_profit as number)}
            </p>
            <p className="text-xs text-[var(--text-tertiary)]">
              {r.margin_pct as number}% margin
            </p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Net Profit
            </p>
            <p
              className={`text-2xl font-bold ${(r.net_profit as number) >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
            >
              {fmt(r.net_profit as number)}
            </p>
            {r.profit_difference !== null && (
              <p className="text-xs text-[var(--text-tertiary)]">
                vs. actual: {(r.profit_difference as number) >= 0 ? "+" : ""}
                {fmt(r.profit_difference as number)}
              </p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function BulkLiquidationSimulator() {
  const [markup, setMarkup] = useState("20");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);

  const simulate = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/whatif/bulk-liquidation?markup_pct=${markup}`,
        )
      ).data,
    onSuccess: (data) => setResult(data),
  });

  const r = result as Record<string, unknown> | null;
  const categories = (r?.by_category || []) as Record<string, unknown>[];

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
          Bulk Liquidation Simulation
        </h3>
        <p className="text-xs text-[var(--text-tertiary)] mb-3">
          What if you sold all unsold inventory at a fixed markup above COGS?
        </p>
        <div className="flex gap-4 items-end">
          <Input
            label="Markup %"
            type="number"
            step="1"
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
          />
          <Button
            size="sm"
            onClick={() => simulate.mutate()}
            isLoading={simulate.isPending}
          >
            <Package className="h-4 w-4" /> Simulate
          </Button>
        </div>
      </Card>

      {r && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Assets
              </p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {r.asset_count as number}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Revenue
              </p>
              <p className="text-xl font-bold text-brand-600">
                {fmt(r.total_revenue as number)}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Profit
              </p>
              <p className="text-xl font-bold text-[var(--text-success)]">
                {fmt(r.total_profit as number)}
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Margin
              </p>
              <p className="text-xl font-bold text-[var(--text-primary)]">
                {r.overall_margin_pct as number}%
              </p>
            </Card>
          </div>

          {categories.length > 0 && (
            <Card>
              <h4 className="mb-2 text-sm font-medium text-[var(--text-primary)]">
                By Category
              </h4>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div
                    key={cat.category as string}
                    className="flex items-center justify-between py-1 border-b border-[var(--color-border-light)]"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="info">{cat.category as string}</Badge>
                      <span className="text-xs text-[var(--text-tertiary)]">
                        {cat.count as number} items
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-[var(--text-success)]">
                        {fmt(cat.total_profit as number)}
                      </span>
                      <span className="ml-2 text-xs text-[var(--text-tertiary)]">
                        {cat.avg_margin_pct as number}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function ReplaySaleSimulator() {
  const [soId, setSoId] = useState("");
  const [adjustment, setAdjustment] = useState("10");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);

  const simulate = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/whatif/replay-sale/${soId}?price_adjustment_pct=${adjustment}`,
        )
      ).data,
    onSuccess: (data) => setResult(data),
  });

  const r = result as Record<string, unknown> | null;
  const actual = r?.actual as Record<string, number> | undefined;
  const hyp = r?.hypothetical as Record<string, number> | undefined;
  const diff = r?.difference as Record<string, number> | undefined;

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
          Replay a Completed Sale
        </h3>
        <p className="text-xs text-[var(--text-tertiary)] mb-3">
          What if you had charged more (or less) on a past sale?
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-end">
          <Input
            label="Sales Order ID"
            value={soId}
            onChange={(e) => setSoId(e.target.value)}
            placeholder="UUID of completed SO"
          />
          <Input
            label="Price Adjustment %"
            type="number"
            step="1"
            value={adjustment}
            onChange={(e) => setAdjustment(e.target.value)}
          />
          <Button
            size="sm"
            onClick={() => simulate.mutate()}
            isLoading={simulate.isPending}
            disabled={!soId}
          >
            <RefreshCw className="h-4 w-4" /> Replay
          </Button>
        </div>
      </Card>

      {r && actual && hyp && diff && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            {
              label: "Total",
              actual: actual.total,
              hyp: hyp.total,
              diff: diff.total,
            },
            {
              label: "Profit",
              actual: actual.total_profit,
              hyp: hyp.total_profit,
              diff: diff.profit,
            },
            {
              label: "Commission",
              actual: actual.commission,
              hyp: hyp.commission,
              diff: diff.commission,
            },
          ].map((m) => (
            <Card key={m.label}>
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                {m.label}
              </p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-sm text-[var(--text-tertiary)] line-through">
                  {fmt(m.actual ?? 0)}
                </span>
                <span className="text-xl font-bold text-[var(--text-primary)]">
                  {fmt(m.hyp ?? 0)}
                </span>
              </div>
              <p
                className={`text-xs mt-0.5 ${(m.diff ?? 0) >= 0 ? "text-[var(--text-success)]" : "text-[var(--text-danger)]"}`}
              >
                {(m.diff ?? 0) >= 0 ? "+" : ""}
                {fmt(m.diff ?? 0)}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default WhatIfPage;
